/**
 * 套餐页。
 *
 * 可购套餐卡片列表 + 购买按钮（purchase）+ 我的套餐列表（有效期/状态）。
 *
 * @module pages/PackagePage
 */
import { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import LoyaltyIcon from '@mui/icons-material/Loyalty';

import PageHeader from '@/components/common/PageHeader';
import MoneyText from '@/components/common/MoneyText';
import DataTable, { type Column } from '@/components/common/DataTable';
import StatusChip from '@/components/common/StatusChip';
import { useToast } from '@/hooks/useToast';
import { useApi } from '@/hooks/useApi';
import { listPackages, getMyPackages, purchasePackage } from '@/services/package';
import type { Package, UserPackage, UserPackageStatus } from '@/types';
import { formatNumber } from '@/utils/format';
import dayjs from 'dayjs';

/** 我的套餐状态元信息。 */
const PKG_STATUS_META: Record<UserPackageStatus, { label: string; color: 'success' | 'default' }> = {
  active: { label: '生效中', color: 'success' },
  expired: { label: '已过期', color: 'default' },
};

/**
 * 套餐页组件。
 *
 * @returns 页面元素。
 */
export default function PackagePage(): JSX.Element {
  const { data: packages, loading: pkgLoading } = useApi(() => listPackages());
  const { data: myPackages, loading: myLoading, refetch } = useApi(() =>
    getMyPackages(),
  );
  const { show, Toast } = useToast();
  const [buyingId, setBuyingId] = useState<number | null>(null);

  const packageMap = useMemo(() => {
    const map = new Map<number, Package>();
    (packages ?? []).forEach((p) => map.set(p.id, p));
    return map;
  }, [packages]);

  const handlePurchase = async (id: number): Promise<void> => {
    setBuyingId(id);
    try {
      await purchasePackage(id);
      show('购买成功');
      await refetch();
    } catch (err) {
      show(err instanceof Error ? err.message : '购买失败', 'error');
    } finally {
      setBuyingId(null);
    }
  };

  const columns: Column<UserPackage>[] = [
    {
      key: 'packageId',
      header: '套餐',
      render: (up) => packageMap.get(up.packageId)?.name ?? `#${up.packageId}`,
    },
    {
      key: 'status',
      header: '状态',
      render: (up) => (
        <StatusChip
          label={PKG_STATUS_META[up.status].label}
          color={PKG_STATUS_META[up.status].color}
        />
      ),
    },
    {
      key: 'expiresAt',
      header: '有效期至',
      render: (up) => dayjs(up.expiresAt).format('YYYY-MM-DD'),
    },
  ];

  return (
    <Box>
      <PageHeader title="套餐" subtitle="按需选购额度包，更划算" />

      <Typography variant="h6" sx={{ mb: 1.5 }}>
        可选套餐
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3,1fr)' },
          gap: 3,
          mb: 4,
        }}
      >
        {(packages ?? []).map((p) => (
          <Card key={p.id}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <LoyaltyIcon color="primary" />
                <Typography variant="h6">{p.name}</Typography>
              </Box>
              <Typography variant="h4" fontWeight={800} color="primary.main">
                <MoneyText value={p.price} />
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                包含 {formatNumber(p.quota)} Tokens · 有效期 {p.validDays} 天
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, minHeight: 44 }}>
                {p.description}
              </Typography>
              <Button
                variant="contained"
                fullWidth
                sx={{ mt: 2 }}
                disabled={buyingId === p.id}
                onClick={() => handlePurchase(p.id)}
              >
                {buyingId === p.id ? '购买中...' : '购买'}
              </Button>
            </CardContent>
          </Card>
        ))}
        {pkgLoading && <Typography color="text.secondary">加载中...</Typography>}
        {!pkgLoading && (packages ?? []).length === 0 && (
          <Typography color="text.secondary">暂无可售套餐</Typography>
        )}
      </Box>

      <Typography variant="h6" sx={{ mb: 1.5 }}>
        我的套餐
      </Typography>
      <DataTable
        columns={columns}
        rows={myPackages ?? []}
        rowKey={(up) => up.id}
        loading={myLoading}
        emptyText="你还没有购买任何套餐"
      />
      {Toast}
    </Box>
  );
}
